import { Dispatch, RefObject } from 'react';
import { GenericTableData, TableScrollAction, TableSelectionAction } from './types';

// Select logic
export const handleSelectOne = <T extends GenericTableData>(
  event: React.ChangeEvent<HTMLInputElement>,
  item: T,
  allItemIds: Array<number>,
  dispatch: Dispatch<TableSelectionAction<T>>
) => {
  if (event.target.checked) {
    dispatch({ type: 'ADD_SELECTED_ITEM', payload: item });
  } else {
    dispatch({
      type: 'REMOVE_SELECTED_ITEM',
      payload: item.id
    });
  }
};

export const handleSelectAllOnPage = <T extends GenericTableData>(
  event: React.ChangeEvent<HTMLInputElement>,
  allItems: T[],
  dispatch: Dispatch<TableSelectionAction<T>>
) => {
  if (event.target.checked) {
    dispatch({ type: 'ADD_ALL_PAGE_ITEMS', payload: allItems });
  } else {
    dispatch({ type: 'REMOVE_ALL_PAGE_ITEMS', payload: allItems.map((i) => i.id) });
  }
};

// Scroll logic

export const handleScroll = (
  tableRef: RefObject<HTMLElement>,
  dispatch: Dispatch<TableScrollAction>
) => {
  if (!tableRef.current) return;
  if (tableRef.current.scrollLeft < 10) {
    dispatch({ type: 'SET_CAN_SCROLL', payload: [false, true] });
  } else if (
    tableRef.current.scrollLeft >
    tableRef.current.scrollWidth - tableRef.current.clientWidth - 10
  ) {
    dispatch({ type: 'SET_CAN_SCROLL', payload: [true, false] });
  } else {
    dispatch({ type: 'SET_CAN_SCROLL', payload: [true, true] });
  }
};

export const handleScrollLeft = (tableRef: RefObject<HTMLElement>) => {
  if (!tableRef.current) return;
  tableRef.current.scrollLeft > 360
    ? (tableRef.current.scrollLeft -= 300)
    : (tableRef.current.scrollLeft = 0);
};

export const handleScrollRight = (tableRef: RefObject<HTMLElement>) => {
  if (!tableRef.current) return;
  tableRef.current.scrollWidth - tableRef.current.clientWidth > 360
    ? (tableRef.current.scrollLeft += 300)
    : (tableRef.current.scrollLeft = tableRef.current.scrollWidth - tableRef.current.clientWidth);
};

export const isScrollable = (element: HTMLElement) => {
  return element && element.scrollWidth > element.clientWidth + 20;
};

export const handleScrollableEnter = (
  tableRef: RefObject<HTMLElement>,
  dispatch: Dispatch<TableScrollAction>
) => {
  if (tableRef.current && isScrollable(tableRef.current))
    dispatch({ type: 'SET_TABLE_HOVERED', payload: true });
};

export const handleScrollableExit = (dispatch: Dispatch<TableScrollAction>) => {
  dispatch({ type: 'SET_TABLE_HOVERED', payload: false });
};

// Visual logic

export const tablePadding = (density: string) => {
  if (density === 'Default') {
    return '22px';
  } else if (density === 'Compact') {
    return '15px';
  } else {
    return '8px';
  }
};

// Reusable table selection state logic

export const removeSelected = <T extends GenericTableData>(
  selectedItems: T[],
  selectedItemIds: Array<number>,
  itemId: number
) => {
  const newSelectedItemIds = selectedItemIds.filter((id) => id !== itemId);
  const newSelectedItems = selectedItems.filter((item) => item.id !== itemId);
  return {
    selectedItemIds: newSelectedItemIds,
    selectedItems: newSelectedItems
  };
};

export const addSelected = <T extends GenericTableData>(
  selectedItems: T[],
  selectedItemIds: Array<number>,
  item: T
) => {
  const newSelectedItemIds = [...selectedItemIds, item.id];
  const newSelectedItems = [...selectedItems, item];
  return {
    selectedItemIds: newSelectedItemIds,
    selectedItems: newSelectedItems
  };
};

export const addAllPageItems = <T extends GenericTableData>(
  selectedItems: T[],
  itemsOnPage: T[]
) => {
  const newSelectedItems = Array.from(new Set([...selectedItems, ...itemsOnPage]));
  const newSelectedItemIds = newSelectedItems.map((item) => item.id);
  return {
    selectedItemIds: newSelectedItemIds,
    selectedItems: newSelectedItems
  };
};

export const removeAllPageItems = <T extends GenericTableData>(
  selectedItems: T[],
  itemsOnPage: Array<number>
) => {
  const newSelectedItems = selectedItems.filter((item) => !itemsOnPage.includes(item.id));
  const newSelectedItemIds = newSelectedItems.map((item) => item.id);
  return {
    selectedItemIds: newSelectedItemIds,
    selectedItems: newSelectedItems
  };
};

export const removeAllSelectedItems = <T extends GenericTableData>(
  selectedItems: T[],
  itemsOnPage: Array<number>
) => {
  const newSelectedItems = selectedItems.filter((item) => !itemsOnPage.includes(item.id));
  const newSelectedItemIds = newSelectedItems.map((item) => item.id);
  return {
    selectedItemIds: newSelectedItemIds,
    selectedItems: newSelectedItems
  };
};
